<template>
  <div class="composite-tab-bar" :style="rootInlineStyle">
    <button
      v-for="(t, idx) in displayTabs"
      :key="(t.tabId != null ? String(t.tabId) : 'tab') + '-' + idx"
      type="button"
      class="tab-btn"
      :class="{ active: isTabActive(t), 'active-template': isTabActive(t) && useActiveTemplate }"
      @click.stop="onSelect(t)">
      <span
        v-if="isTabActive(t) && useActiveTemplate && activeTemplateDataUrl"
        class="tab-btn-active-wrap">
        <img
          class="tab-btn-active-template"
          :src="activeTemplateDataUrl"
          alt=""
          :style="activeTemplateImgStyle">
      </span>
      <span class="tab-btn-label">{{ tabLabelText(t) }}</span>
    </button>
  </div>
</template>

<script>
import { imgURLtoBase64 } from '@/utils/common'
import { getActiveTabId, setActiveTabId, initActiveTabIfEmpty } from '@/utils/compositeTabState'
import {
  mergeCompositeTabBarBarStyle,
  COMPOSITE_TAB_BAR_ACTIVE_TEMPLATE_URL,
} from '@/utils/compositeScreen'
import { getFontFamilyCss, getBorderTemplateImageStyle } from '@/utils/chartTitlePresets'

export default {
  name: 'CompositeTabBar',
  props: {
    config: { type: Object, required: true },
    globalConfig: { type: Object, default: null },
    componentType: { type: String, default: '' },
    componentStatus: { type: String, default: 'view' },
    /** 嵌套渲染时隔离 tab 激活态，避免不同组合屏串状态 */
    tabScopeId: { type: [String, Number], default: 'global' },
  },
  data () {
    return {
      activeTemplateDataUrl: '',
    }
  },
  computed: {
    groupId () {
      return this.config?.chartSet?.groupId || ''
    },
    tabs () {
      return Array.isArray(this.config?.chartSet?.tabs) ? this.config.chartSet.tabs : []
    },
    displayTabs () {
      if (this.componentStatus === 'edit') {
        return this.tabs
      }
      return this.tabs.filter(t => {
        if (!t) return false
        const tt = this.tabType(t)
        if (tt === 'url') return !!this.tabUrlString(t)
        return !!this.tabPanelSnString(t)
      })
    },
    activeTabIdStr () {
      const id = getActiveTabId(this.groupId, this.tabScopeId)
      return id == null || id === '' ? '' : String(id)
    },
    bar () {
      return mergeCompositeTabBarBarStyle(this.config?.chartSet?.barStyle)
    },
    useActiveTemplate () {
      return this.bar.activePresentation === 'template'
    },
    activeTemplateImgStyle () {
      return getBorderTemplateImageStyle({
        borderTemplateStretch: this.bar.activeTemplateStretch !== false,
      })
    },
    rootInlineStyle () {
      const b = this.bar
      const ff = getFontFamilyCss(b.fontFamily)
      const gap = typeof b.tabGap === 'number' && Number.isFinite(b.tabGap) ? `${b.tabGap}px` : (b.tabGap || '8px')
      const radius = Number(b.tabBorderRadius)
      const tabRadiusPx = Number.isFinite(radius) && radius >= 0 ? `${radius}px` : '4px'
      const tpl = b.activePresentation === 'template'
      return {
        background: b.barBackground,
        padding: b.barPadding,
        gap,
        fontSize: `${Number(b.fontSize) || 14}px`,
        color: b.color,
        fontFamily: ff || undefined,
        fontWeight: b.fontWeight || '400',
        '--ctb-tab-pad': b.tabPadding || '4px 12px',
        '--ctb-tab-radius': tabRadiusPx,
        '--ctb-tab-border': b.tabBorder || 'none',
        '--ctb-tab-bg': b.tabBackground,
        '--ctb-active-color': b.activeColor,
        '--ctb-active-bg': tpl ? 'rgba(0, 0, 0, 0)' : b.activeBackground,
        '--ctb-active-border': tpl ? 'none' : (b.activeBorder || 'none'),
        '--ctb-active-weight': b.activeFontWeight || '600',
      }
    },
  },
  watch: {
    useActiveTemplate: {
      immediate: true,
      handler (on) {
        if (on) {
          this.loadActiveTemplateImg()
        } else {
          this.activeTemplateDataUrl = ''
        }
      },
    },
    displayTabs: {
      immediate: true,
      deep: true,
      handler (tabs) {
        if (!this.groupId) return
        const first = Array.isArray(tabs) && tabs.length && tabs[0] ? tabs[0] : null
        if (first && first.tabId != null && first.tabId !== '') {
          initActiveTabIfEmpty(this.groupId, String(first.tabId), this.tabScopeId)
        }
      },
    },
  },
  methods: {
    /** 仅展示用户填写的标签；空则不展示任何文字（不再用 tabId 兜底） */
    tabLabelText (t) {
      if (!t || t.label == null) return ''
      const s = String(t.label).trim()
      return s
    },
    tabType (t) {
      const v = t && t.type != null ? String(t.type).trim().toLowerCase() : ''
      return v === 'url' ? 'url' : 'panel'
    },
    tabPanelSnString (t) {
      if (!t) return ''
      const raw = t.panelSn ?? t.panel_sn
      if (raw == null || raw === '') return ''
      if (typeof raw === 'string' || typeof raw === 'number') return String(raw)
      if (typeof raw === 'object') {
        if (raw.panelSn != null && raw.panelSn !== '') return String(raw.panelSn)
        if (raw.value != null && raw.value !== '') return String(raw.value)
      }
      return ''
    },
    tabUrlString (t) {
      if (!t) return ''
      const raw = t.url
      if (raw == null || raw === '') return ''
      return String(raw).trim()
    },
    isTabActive (t) {
      return String(t.tabId) === this.activeTabIdStr
    },
    async loadActiveTemplateImg () {
      try {
        this.activeTemplateDataUrl = await imgURLtoBase64(COMPOSITE_TAB_BAR_ACTIVE_TEMPLATE_URL)
      } catch (e) {
        this.activeTemplateDataUrl = ''
      }
    },
    onSelect (t) {
      if (!this.groupId || !t) return
      setActiveTabId(this.groupId, String(t.tabId), this.tabScopeId)
    },
  },
}
</script>

<style lang="scss" scoped>
.composite-tab-bar {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  width: 100%;
  height: 100%;
  box-sizing: border-box;
}
.tab-btn {
  position: relative;
  cursor: pointer;
  margin: 0;
  font: inherit;
  appearance: none;
  -webkit-appearance: none;
  border: var(--ctb-tab-border, none);
  background: var(--ctb-tab-bg, transparent);
  color: inherit;
  border-radius: var(--ctb-tab-radius, 4px);
  padding: var(--ctb-tab-pad, 4px 12px);
  line-height: 1.35;
  outline: none;
  height: 100%;
}
.tab-btn-label {
  position: relative;
  z-index: 1;
}
.tab-btn-active-wrap {
  position: absolute;
  inset: 0;
  z-index: 0;
  pointer-events: none;
  border-radius: inherit;
  overflow: hidden;
  display: flex;
  align-items: center;
  justify-content: center;
  transform: translateY(7px);
}
.tab-btn.active-template {
  overflow: hidden;
}
.tab-btn:focus-visible {
  outline: 1px dashed rgba(64, 158, 255, 0.6);
  outline-offset: 2px;
}
.tab-btn.active {
  color: var(--ctb-active-color, #409eff);
  background: var(--ctb-active-bg, transparent);
  border: var(--ctb-active-border, none);
  font-weight: var(--ctb-active-weight, 600);
}
</style>
